import { Control, UseFormSetValue } from "react-hook-form";
import {
  RelatorioPrestacaoContasForm,
  RelatorioPrestacaoContasItem,
  TipoItemRelatorioPrestacaoContas,
} from "core/features/relatorios/prestacao-contas/typings";
import { RelatorioIdEnum } from "core/features/relatorios/typings";
import { Row, Col, Modal } from "antd";
import { Box, Text, Flex, Checkbox, CheckboxGroup } from "@chakra-ui/react";
import Field from "components/Input/Field";
import FooterModal from "components/Button/FooterModal";
import Toolbar from "components/Toolbar";
import SelectTipoFiltroDataRelatorio from "./SelectTipoFiltroDataRelatorio";
import FormAssinaturas from "components/Relatorio/FormAssinaturas";
import moment from "moment";
import { TipoFiltroDataRelatorioPrestacaoContas } from "features/relatorios/prestacao-contas/typings";
import SelectMonthYear from "ui/components/Input/SelectMonthYear";
import SelectOrientacaoCapaRelatorio from "./SelectOrientacaoCapaRelatorio";

interface ModalConfiguracaoAdicionalProps {
  isOpen: boolean;
  onClose: () => any;
  index: number;
  colunas?: {
    header: string;
    items: {
      value: string;
      label: string;
    }[];
  }[];
  ComponentFiltrosAdicionais?: React.ReactNode;
  item: Partial<RelatorioPrestacaoContasItem>;
  control: Control<RelatorioPrestacaoContasForm, any>;
  setValue: UseFormSetValue<RelatorioPrestacaoContasForm>;
}

function ModalConfiguracaoAdicional({
  isOpen,
  onClose,
  index,
  colunas,
  item,
  control,
  ComponentFiltrosAdicionais,
  setValue,
}: ModalConfiguracaoAdicionalProps) {
  const isRelatorio =
    item.tipoItem === TipoItemRelatorioPrestacaoContas.Relatorio;

  const isCapa = item.tipoItem === TipoItemRelatorioPrestacaoContas.Capa;

  const mesEspecifico =
    item.tipoFiltroData ===
    TipoFiltroDataRelatorioPrestacaoContas.MesEspecifico;

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          hidePrimaryButton
          showRegisterAnother={false}
          isLoadingSave={false}
          onClose={onClose}
          buttonCancelarText="Fechar"
        />
      }
      onCancel={onClose}
      width="800px"
      bodyStyle={{
        height: "55vh",
        overflow: "auto",
        padding: 16,
        borderRadius: 16,
      }}
      forceRender
      className="antd-custom-header"
      title={
        <Toolbar
          title="Configurações adicionais do item"
          onClose={onClose}
          border="0px"
          alignItems="center"
        />
      }
      closable={false}
    >
      <Flex flexDir="column">
        <Box as={Row} gutter={[16, 8]} mb={4}>
          <Box as={Col} xs={12}>
            <Field
              control={control}
              name={`items.${index}.tipoFiltroData`}
              label="Período"
              rules={{
                required:
                  item.tipoItem !== TipoItemRelatorioPrestacaoContas.PdfExterno,
              }}
              render={({ field }) => (
                <SelectTipoFiltroDataRelatorio
                  {...field}
                  onChange={(v) => {
                    if (
                      v === TipoFiltroDataRelatorioPrestacaoContas.MesEspecifico
                    ) {
                      setValue(
                        `items.${index}.parametros.filtrosAdicionais.mesEspecifico`,
                        moment().format("YYYY-MM")
                      );
                    }
                    field.onChange(v);
                  }}
                  allowClear={false}
                  anual={
                    isRelatorio &&
                    item.relatorioId === RelatorioIdEnum.DemonstrativoAnual
                  }
                  includeAnual={
                    ![RelatorioIdEnum.BalancoFinanceiro].includes(
                      item.relatorioId!
                    )
                  }
                  includeTodosOsMeses={
                    ![RelatorioIdEnum.BalancoFinanceiro].includes(
                      item.relatorioId!
                    )
                  }
                />
              )}
            />
          </Box>

          {mesEspecifico ? (
            <Box as={Col} xs={12}>
              <Field
                control={control}
                name={`items.${index}.parametros.filtrosAdicionais.mesEspecifico`}
                label="Mês"
                render={({ field }) => (
                  <SelectMonthYear
                    bg="white"
                    value={
                      field.value ? moment(field.value, "YYYY-MM") : moment()
                    }
                    onChange={(date) => field.onChange(date?.format("YYYY-MM"))}
                    style={{
                      width: "100%",
                    }}
                    getPopupContainer={(node: any) => node.parentNode!}
                  />
                )}
              />
            </Box>
          ) : null}

          {isCapa ? (
            <Box as={Col} xs={12}>
              <Field
                control={control}
                name={`items.${index}.orientacao`}
                shouldUnregister
                label="Orientação da capa"
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <SelectOrientacaoCapaRelatorio
                    {...field}
                    allowClear={false}
                  />
                )}
              />
            </Box>
          ) : null}
        </Box>

        {ComponentFiltrosAdicionais && item?.relatorioId ? (
          <Box as={Row} gutter={16} mb={4}>
            <Box as={Col} xs={24}>
              {ComponentFiltrosAdicionais}
            </Box>
          </Box>
        ) : null}

        {!isCapa ? (
          <FormAssinaturas
            control={control}
            setValue={setValue}
            formAssinaturas={item.parametros?.assinaturas}
            namePrefix={`items.${index}.parametros`}
          />
        ) : null}

        {colunas?.length ? (
          <Field
            control={control}
            name={`items.${index}.parametros.colunas`}
            render={({ field }) => (
              <>
                <Text as="h3" fontWeight="500" fontSize={16}>
                  Colunas do relatório
                </Text>
                <CheckboxGroup value={field.value} onChange={field.onChange}>
                  <Flex gridGap={4}>
                    {colunas?.map((coluna, index) => (
                      <Flex flexDir="column" gridGap={2} key={index}>
                        <Text as="h3" fontWeight="500">
                          {coluna.header}
                        </Text>

                        {coluna?.items?.map((item) => (
                          <Checkbox key={item.value} value={item.value}>
                            {item.label}
                          </Checkbox>
                        ))}
                      </Flex>
                    ))}
                  </Flex>
                </CheckboxGroup>
              </>
            )}
          />
        ) : null}
      </Flex>
    </Modal>
  );
}

export default ModalConfiguracaoAdicional;
